import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState, useEffect } from "react";
import warning from "../../resources/icons/red-warning.svg";
import BlueWarning from "../../resources/icons/blue-warning.svg";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { GET } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import CfLoader from "../../common/CfLoader/CfLoader";
import EntityConstants from "../../configs/EntityConstants";
import DOMPurify from "dompurify";

export default function VerificationApprovalModal({
  isModalOpen,
  toggleModal,
  actionType,
  handleConfirmAction,
  issuerComments,
  setIssuerComments,
  transaction_id,
  isLegacy = false,
  matchingVCs = [],
  vcStatus,
  testId = "verification-approval-modal",
}) {
  const { t } = useTranslation();
  const commentsInputId = `${testId}-comments`;

  const [htmlContent, setHtmlContent] = useState("");
  const [isLoadingHtml, setIsLoadingHtml] = useState(false);
  const [hasFetchedHtml, setHasFetchedHtml] = useState(false);

  useEffect(() => {
    if (
      isModalOpen &&
      actionType === "Approved" &&
      transaction_id &&
      !hasFetchedHtml
    ) {
      fetchHtmlPreview();
    }

    if (!isModalOpen) {
      setHasFetchedHtml(false);
      setHtmlContent("");
    }
  }, [isModalOpen, actionType, transaction_id, hasFetchedHtml]);

  function closeModal() {
    toggleModal(false);
  }

  const fetchHtmlPreview = async () => {
    setIsLoadingHtml(true);
    try {
      const response = await GET(
        `${EntityConstants.GET_VERIFICATION_REPORT_EMAIL_TEMPLATE}?transaction_id=${transaction_id}`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const htmlText = await response.text();
      setHtmlContent(htmlText);
      setHasFetchedHtml(true);
    } catch (error) {
      console.error("Error fetching HTML preview:", error);
      Toast({
        type: "error",
        title: t("verificationApprovals:error"),
        message: t("verificationApprovals:failed_to_fetch_html_preview"),
      });
    } finally {
      setIsLoadingHtml(false);
      setHasFetchedHtml(true);
    }
  };

  const getConfirmationMessage = () => {
    if (actionType === "Approved" && isLegacy && matchingVCs.length === 0) {
      return `${t(
        "verificationApprovals:no_matching_credentials_found_approve",
      )}`;
    } else if (actionType === "Approved") {
      return `${t("verificationApprovals:are_you_sure_approve_request")}`;
    } else {
      return `${t("verificationApprovals:are_you_sure_reject_request")}`;
    }
  };

  const isConfirmDisabled = () => {
    return !issuerComments || !issuerComments.trim();
  };

  if (!actionType) {
    return null;
  }

  return (
    <>
      <Transition appear show={isModalOpen ? true : false} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              id={`${testId}-backdrop`}
              className="fixed inset-0 bg-black/25 backdrop-blur-md"
            />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  id={testId}
                  className={`w-full ${actionType === "Approved" ? "max-w-4xl" : "max-w-md"} transform overflow-hidden rounded-2xl bg-cfSecondary p-6 text-left align-middle shadow-xl transition-all relative`}
                >
                  <button
                    type="button"
                    id={`${testId}-close`}
                    className="absolute top-3 right-3 text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={closeModal}
                  >
                    <span className="sr-only">Close</span>
                    &#10005;
                  </button>
                  <Dialog.Title
                    as="h3"
                    className="text-lg flex justify-center font-medium leading-6 text-gray-900"
                  >
                    <img
                      src={
                        actionType === "Approved"
                          ? BlueWarning
                          : actionType === "Rejected"
                            ? warning
                            : warning
                      }
                      alt="warning"
                    />
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-lg font-medium flex justify-center">
                      {actionType === "Approved"
                        ? t("verificationApprovals:approve")
                        : t("verificationApprovals:reject")}
                    </p>
                    <p className="text-sm text-gray-500 mt-2 flex justify-center">
                      {getConfirmationMessage()}
                    </p>

                    {/* Only show email preview for Approve action */}
                    {actionType === "Approved" && (
                      <div className="mt-2">
                        <h4 className="text-md font-semibold text-gray-900 mb-2">
                          {t("verificationApprovals:email_preview")}
                        </h4>
                        <div className="bg-white p-4 rounded-lg border border-gray-200 max-h-[30rem] overflow-y-auto">
                          {isLoadingHtml ? (
                            <div className="flex items-center justify-center h-32">
                              <CfLoader />
                            </div>
                          ) : htmlContent ? (
                            <div
                              className="html-preview-container overflow-auto text-sm"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(htmlContent),
                              }}
                            />
                          ) : (
                            <div className="text-center py-8">
                              <p className="text-gray-500">
                                {t(
                                  "verificationApprovals:no_html_content_available",
                                )}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {vcStatus === "Revoked" && actionType === "Approved" && (
                      <div className="p-3 bg-yellow-50 border-yellow-200 rounded-lg my-3">
                        <div className="flex items-center">
                          <div className="mr-3 text-cfIssuedCredentialTemplateCardStatusUnderReviewBG">
                            <AlertTriangle className="h-5 w-5" />
                          </div>
                          <p className="text-sm font-medium text-cfIssuedCredentialTemplateCardStatusUnderReviewBG">
                            {t(
                              "verificationApprovals:credential_revoked_approve",
                            )}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="mt-3">
                      <label
                        htmlFor={commentsInputId}
                        className="block text-sm font-medium text-gray-700 mb-0.5"
                      >
                        {actionType === "Approved"
                          ? t(
                              "verificationApprovals:please_add_comment_approve",
                            )
                          : t(
                              "verificationApprovals:please_provide_reason_rejection",
                            )}
                      </label>
                      <textarea
                        id={commentsInputId}
                        value={issuerComments}
                        onChange={(e) => setIssuerComments(e.target.value)}
                        className={`w-full px-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 ${
                          actionType === "Approved"
                            ? "focus:ring-blue-500"
                            : "focus:ring-red-500"
                        }`}
                        rows={3}
                        placeholder={
                          actionType === "Approved"
                            ? t("verificationApprovals:enter_approval_comment")
                            : t("verificationApprovals:enter_rejection_reason")
                        }
                        style={{ fontSize: "0.875rem" }}
                        required
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex justify-center gap-4">
                    <button
                      id={`${testId}-cancel`}
                      data-modal-hide="popup-modal"
                      type="button"
                      onClick={closeModal}
                      className="py-2.5 px-5 ms-3 text-sm hover:bg-gray-200 font-medium text-gray-900 focus:outline-none bg-cfSecondary rounded-lg border border-gray-200 focus:z-10 focus:ring-3 focus:ring-gray-100"
                    >
                      {t("verificationApprovals:cancel")}
                    </button>
                    <button
                      id={`${testId}-confirm`}
                      data-modal-hide="popup-modal"
                      type="button"
                      onClick={handleConfirmAction}
                      className={`text-cfSecondary focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm inline-flex items-center px-5 py-2.5 text-center transition-colors ${
                        isConfirmDisabled()
                          ? actionType === "Approved"
                            ? "bg-blue-300 cursor-not-allowed opacity-70"
                            : "bg-red-300 cursor-not-allowed opacity-70"
                          : actionType === "Approved"
                            ? "bg-cfPrimaryButton hover:bg-[#2a2da9]"
                            : "bg-[#D94D40] hover:bg-[#b84035]"
                      }`}
                      disabled={isConfirmDisabled()}
                    >
                      {actionType === "Approved"
                        ? t("verificationApprovals:approve")
                        : t("verificationApprovals:reject")}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
